import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog, faArrowLeft, faSpinner, faToggleOn, faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';

interface Setting {
  key: string;
  value: boolean;
  description: string;
}

const AdminSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      const settingsData = response.data.settings || {};
      
      // Convert settings object to array
      const settingsArray: Setting[] = Object.keys(settingsData).map(key => ({
        key,
        value: settingsData[key].value,
        description: settingsData[key].description || ''
      }));
      
      setSettings(settingsArray);
    } catch (error: any) {
      console.error('Failed to fetch settings:', error);
      setError(error.response?.data?.error || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key: string, currentValue: boolean) => {
    setSaving(key);
    setError('');
    
    try {
      const newValue = !currentValue;
      const response = await api.put(`/settings/${key}`, { value: newValue });
      
      // Update local state
      setSettings(prev => 
        prev.map(setting => 
          setting.key === key 
            ? { ...setting, value: response.data.value }
            : setting
        )
      );
    } catch (error: any) {
      console.error('Failed to update setting:', error);
      setError(error.response?.data?.error || 'Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center py-12">
        <div className="gp-spinner" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="gp-page py-6">
      <div className="gp-container max-w-3xl">
        <Link to="/dashboard" className="gp-back-link mb-4">
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          {t('common.backToDashboard')}
        </Link>

        <div className="gp-page-header">
          <div className="flex items-center gap-2 mb-1">
            <span className="gp-icon-tile">
              <FontAwesomeIcon icon={faCog} aria-hidden="true" />
            </span>
            <h1 className="gp-page-title">Admin Settings</h1>
          </div>
          <p className="gp-page-subtitle">
            Manage application-wide settings and feature flags
          </p>
        </div>

        {error && (
          <div className="gp-alert-error" role="alert">
            {error}
          </div>
        )}

        <div className="gp-card">
          {settings.length === 0 ? (
            <div className="text-center py-10">
              <FontAwesomeIcon icon={faCog} className="text-3xl gp-text-muted mb-3" aria-hidden="true" />
              <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                No Settings Found
              </h3>
              <p className="text-sm gp-text-muted">
                Settings will appear here once configured in the database
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {settings.map((setting) => (
                <div
                  key={setting.key}
                  className="flex items-center justify-between p-3 rounded-md transition-colors"
                  style={{ border: '1px solid var(--gp-border)' }}
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold mb-0.5" style={{ color: 'var(--gp-ink)' }}>
                      {setting.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h3>
                    {setting.description && (
                      <p className="text-xs gp-text-muted">
                        {setting.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="ml-3 flex items-center shrink-0">
                    {saving === setting.key ? (
                      <FontAwesomeIcon 
                        icon={faSpinner} 
                        className="animate-spin text-lg"
                        style={{ color: 'var(--gp-brand)' }}
                        aria-hidden="true"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleToggle(setting.key, setting.value)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                        style={
                          setting.value
                            ? { background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }
                            : { background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }
                        }
                      >
                        <FontAwesomeIcon 
                          icon={setting.value ? faToggleOn : faToggleOff} 
                          className="text-lg" 
                          aria-hidden="true"
                        />
                        <span>
                          {setting.value ? 'Enabled' : 'Disabled'}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
